'use client'

import { ProductStatusLine } from '@/feature/product/components/product-availabilities/ProductStatusLine.client'
import { useRefreshProductBuyBoxPriceAndStock } from '@/feature/product/hooks/useRefreshProductBuyBoxPriceAndStock'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { AccordionItem } from '@/ui/accordion/AccordionItem'
import { Badge } from '@/ui/badge/Badge'
import { Button } from '@/ui/buttons/button/Button.client'
import { Card } from '@/ui/cards/card/Card'
import { TextSkeleton } from '@/ui/skeleton/TextSkeleton'
import { StatusDotSkeleton } from '@/ui/status-dot/StatusDot'
import { gql } from '@apollo/client'
import type {
  ProductAvailabilityItem_AvailabilityFragment,
  SelectablePointOfSaleCard_V2_PointOfSaleFragment,
} from '@gql-generated/graphql'
import type React from 'react'
import { useCallback, useState, useTransition } from 'react'
import { createPointOfSaleSelectedIntent } from '../../services/pointOfSale.intents'
import { PointOfSaleOpeningHoursList } from '../point-of-sale-opening-hours-list/PointOfSaleOpeningHoursList'

gql(/* GraphQL */ `
  fragment SelectablePointOfSaleCard_V2_pointOfSale on PointOfSale {
    id
    name
    activity
    distance
    parentId
    address {
      line1
      postalCode
      city
    }
    openingHours {
      day
      dayOfWeek
      intervals {
        begin
        end
      }
    }
    exceptionalDates {
      date
      isClosed
    }
    subsidiary {
      id
      name
    }
  }
`)

export type SelectablePointOfSaleProps = {
  /* The point of sale to display */
  pointOfSale: SelectablePointOfSaleCard_V2_PointOfSaleFragment

  /* Whether or not the card is selectable */
  isSelectable?: boolean

  /*
   * If true:  displays a tag showing the card is selected and hides the select button
   * If false: displays a button to select the card
   */
  isSelected?: boolean

  /* Whether or not the button is disabled */
  isDisabled?: boolean

  /* Whether or not to display the activity of the point of sale */
  displayActivity?: boolean

  /* The server action to perform when the button is clicked */
  onPointOfSaleSelected?: (
    posId: SelectablePointOfSaleCard_V2_PointOfSaleFragment,
  ) => Promise<void> | void
} & (
  | {
      displayStock?: true
      isStockLoading?: boolean
      availability?: ProductAvailabilityItem_AvailabilityFragment
    }
  | {
      displayStock?: false
      isStockLoading?: never
      availability?: never
    }
)

export function SelectablePointOfSaleCard({
  availability,
  pointOfSale,
  displayStock,
  isSelected,
  isDisabled,
  isSelectable,
  isStockLoading,
  displayActivity,
  ...props
}: SelectablePointOfSaleProps): React.ReactNode {
  const [isExpanded, setIsExpanded] = useState(false)

  const { onPointOfSaleSelected } = props

  const { t } = useTranslation(['selectablePointOfSaleCard', 'productAvailabilities'])
  const [isActionPending, startTransition] = useTransition()

  const toggleOpeningHours = useCallback(() => setIsExpanded((prev) => !prev), [])

  const refreshProductBuyBoxPriceAndStock = useRefreshProductBuyBoxPriceAndStock()

  return (
    <Card padding="md" radius="sm" skin="body-alt" className="flex w-full flex-col gap-2">
      <div className="flex flex-col gap-1">
        {isSelected ? (
          <Badge tint="brand" size="sm" className="mr-auto">
            {t(`selectablePointOfSaleCard:selectedStore`)}
          </Badge>
        ) : displayActivity && pointOfSale.activity ? (
          <Badge tint="brand" size="sm" className="mr-auto">
            {pointOfSale.activity}
          </Badge>
        ) : null}

        <div className="flex flex-col gap-0.5">
          <div className="flex justify-between gap-2">
            <span className="line-clamp-2 text-lg font-bold">{pointOfSale.name}</span>
            <span className="whitespace-nowrap leading-light">
              {pointOfSale.distance && `${pointOfSale.distance?.toFixed(1)} km`}
            </span>
          </div>

          {displayStock && isStockLoading && (
            <span className="flex items-center gap-1">
              <StatusDotSkeleton />
              <TextSkeleton size="md" className="w-18" leading="normal" />
            </span>
          )}

          {displayStock && !isStockLoading && availability && (
            <ProductStatusLine
              availability={availability}
              canAddToCart={false}
              clickAndCollectStatus={null}
              type="store"
            />
          )}

          <div>
            <span className="inline-block leading-[1.2]" data-testid="address/line-1">
              {pointOfSale.address.line1}
            </span>
            <br />
            <span data-testid="address/postal-code">{pointOfSale.address.postalCode}</span>{' '}
            <span data-testid="address/city">{pointOfSale.address.city}</span>
          </div>
        </div>

        {!!pointOfSale.openingHours?.length && (
          <AccordionItem
            id="opening-hours"
            className="mt-1 [&_svg]:mr-auto"
            onExpandedChange={toggleOpeningHours}
          >
            <AccordionItem.Header
              title={
                isExpanded
                  ? t('selectablePointOfSaleCard:openingHours.hide')
                  : t('selectablePointOfSaleCard:openingHours.show')
              }
              compact
              className="p-0"
            />
            <AccordionItem.Panel className="mx-0 px-0">
              <PointOfSaleOpeningHoursList
                className="mt-1"
                showTitle={false}
                dayOpeningHours={pointOfSale.openingHours!}
                exceptionalDates={pointOfSale.exceptionalDates || []}
              />
            </AccordionItem.Panel>
          </AccordionItem>
        )}
      </div>

      {isSelectable !== false && !isSelected && (
        <Button
          isLoading={isActionPending}
          isDisabled={isDisabled && !isActionPending}
          variant="secondary"
          size="lg"
          intent={INTENT_SAME_AS_WEB}
          onPress={() => {
            startTransition(async () => {
              await onPointOfSaleSelected?.(pointOfSale)
              refreshProductBuyBoxPriceAndStock()
              handleIntent(createPointOfSaleSelectedIntent(pointOfSale.id))
            })
          }}
        >
          {t('selectablePointOfSaleCard:selectStoreAction')}
        </Button>
      )}
    </Card>
  )
}
